/* eslint-disable @typescript-eslint/member-ordering */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from } from 'rxjs';

import { DexieService } from './dexieService';
import { HttpUtils } from '../utils/httpUtils';
import { Injectable } from '@angular/core';
import { OnlineService } from './online.service';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { ExecutionAttachmentFilter, ExecutionAuditFilter, ExecutionQ, ExecutionQFilter, ExecutionQTestEssay } from '../model/execution';
import * as _ from 'lodash';
import { DateUtils } from '../utils/dateUtils';
import { ExecutionReportFilter } from '../model/attachment';

@Injectable({
    providedIn: 'root'
})
export class ExecutionQService {

    readonly baseUrl = `${environment.qualificatesUrl}/execution`;
    constructor(private http: HttpClient,
        private dexieService: DexieService,
        private onlineService: OnlineService) {
    }

    findAll(filter: ExecutionQFilter): Observable<any> {
        const url = `${this.baseUrl}/`;
        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getExcel(filter: ExecutionQFilter): Observable<any> {
        const url =  `${this.baseUrl}/excel`;

        return this.http.post(url, filter, { responseType: 'blob' });
    }

    findAllAssignable(filter: ExecutionQFilter): Observable<any> {
        const url = `${this.baseUrl}/assignable`;

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    findOne(id: number): Observable<any> {
        const url =  `${this.baseUrl}/${id}`;
        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    save(execution: ExecutionQ, token: string): Observable<any> {
        return this.genericSave(execution, token, null);
    }
    
    delete(id: number): Observable<any> {
        const url =  `${this.baseUrl}/${id}`;

        return this.http.delete(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    private genericSave(execution: ExecutionQ, token: string, action?: string): Observable<any> {
        let url =  `${this.baseUrl}/`;
        if (execution.id != null && execution.id != 0) {
            url += execution.id;
            
            if (action != null) {
                url += action;
            }

            for (let index = 0; index < execution.executionQTests?.length; index++) {
                const element = execution.executionQTests[index];

                for (let indexGroup = 0; indexGroup < element.executionQTestEssayGroup?.length; indexGroup++) {
                    const elementGroup =  element.executionQTestEssayGroup[indexGroup];

                    for (let indexValues = 0; indexValues < elementGroup.essayValues?.length; indexValues++) {
                        const elementValue = elementGroup.essayValues[indexValues];
                        if (elementValue.fcStartEssay != null) {
                            elementValue.fcStartEssay = DateUtils.anyToDate(elementValue.fcStartEssay as Date);
                            elementValue.fcStartEssay = new Date(`${elementValue.fcStartEssay.toString().split('GMT')[0]} UTC`).toISOString()
                                .split('.')[0] + '.000+0000';
                        }
        
                        if (elementValue.fcEndEssay != null) {
                            elementValue.fcEndEssay = DateUtils.anyToDate(elementValue.fcEndEssay as Date);
                            elementValue.fcEndEssay = new Date(`${elementValue.fcEndEssay.toString().split('GMT')[0]} UTC`).toISOString()
                                .split('.')[0] + '.000+0000';
                        }
                        
                        if(elementValue.sensors != null){
                            elementValue.sensors?.forEach(s => {
                                delete s.data;
                            });
                         }
                    }
                    
                }
                
            }
            return this.http.put(url, execution).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            url += 'create';

            return this.http.post(url, {
                execution: execution,
                token: token
            }).pipe(map(httpRes =>
                 HttpUtils.extractData(httpRes)));
        }
    }

    getAudit(id: number, filter: ExecutionAuditFilter): Observable<any> {
        
        if (this.onlineService.latestOnline) {
            const url =  `${this.baseUrl}/${id}/audit`;
            return this.http.post(url, filter).pipe(map(httpRes =>  HttpUtils.extractData(httpRes)));
        } else {
            return from([]);
        }

    }

    getAttachments(id: number, filter: ExecutionAttachmentFilter): Observable<any> {
        const url =  `${this.baseUrl}/${id}/attachment`;

        return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }
    
    validateTest(id: number, idTestType: number, reason: string): Observable<any> {
        const url =  `${this.baseUrl}/${id}/${idTestType}/test/validate`;

        const body = { reason };

        return this.http.put(url, body).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    validate(id: number, token: string, forceAccording: boolean, reasonForce: string): Observable<any> {
        const url = `${this.baseUrl}/${id}/validate`;

        const body = { forceAccording, reasonForce, token };

        return this.http.put(url, body).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadDeviationPdf(id: number, config: ExecutionReportFilter = new ExecutionReportFilter()): Observable<any> {
        const url = environment.qualificatesUrl + `/report/execution/deviation/${id}`;

        const headers = new HttpHeaders({
            lang: config.lang
        });

        const data = {
            reason: config.reason,
            reviewBy: config.reviewBy,
            toSign: config.toSign,
            hybride: config.hybride
        };

        return this.http.post(url, data, { responseType: 'blob', headers });
    }

    downloadPdf(id: number, config: ExecutionReportFilter = new ExecutionReportFilter()): Observable<any> {
        const url = environment.qualificatesUrl + `/report/execution/${id}`;

        const headers = new HttpHeaders({
            lang: config.lang
        });

        const data = {
            graphsToInclude: config.graphsToInclude,
            dataTableInclude: config.dataTableInclude,
            reviewBy: config.reviewBy,
            toSign: config.toSign,
            reason: config.reason,
            conclusions: config.conclusions,
            hybride: config.hybride
        };

        return this.http.post(url, data, { headers }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadPdfExecutionVersion(execution: ExecutionQ): Observable<any> {
        return this.downloadPdfVersion(execution.id, execution.currentVersion);
    }

    downloadPdfVersion(id: number, version: number): Observable<any> {
        const url = environment.qualificatesUrl + `/report/execution/${id}/version/${version}`;

        return this.http.post(url, {}, { responseType: 'blob' });
    }

    checkForAdvance(id: number): Observable<any> {
        const url = environment.qualificatesUrl + `/execution/${id}/check`;

        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }


    invalidateByDoc(id: number, reason: string, token: string): Observable<any> {
        const url = `${this.baseUrl}/${id}/invalidateByDoc`;

        const body = { reason, token };

        return this.http.put(url, body).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getConclusions(id: number, lang: string): Observable<any> {
        const url = `${this.baseUrl}/${id}/conclusions`;

        const headers = new HttpHeaders({
            lang
        });

        return this.http.get(url, { headers }).pipe(map(httpRes => HttpUtils.extractData(httpRes)));

    }
    
}
